import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "../../../../stores/RootStore";

export interface TrailerDimensions {
  length: number; // in feet
  width: number;  // in feet
  maxWeight: number; // in pounds
}

export interface LoadedPanel {
  panelId: string;
  position: { x: number; y: number };
  rotation: 0 | 90 | 180 | 270;
  stackOrder: number;
}

export interface TrailerConfiguration {
  id: string;
  name: string;
  type: 'flatbed' | 'step-deck' | 'lowboy';
  length: number;
  width: number;
  deckHeight: number;
  maxCapacity: number;
  axleConfiguration: {
    front: { position: number; maxLoad: number };
    rear: { position: number; maxLoad: number };
  };
}

export interface PanelDimensions {
  width: number;
  length: number;
  thickness: number;
  weight: number;
}

export class TrailerLoadViewStore {
  // Observables - UI State Only
  activeLoadId: string | null = null;
  selectedPanelId: string | null = null;
  selectedTrailerConfigId: string | null = null;
  viewMode: 'top' | 'side' | '3d' = 'top';
  zoomLevel: number = 1;
  panPosition: { x: number; y: number } = { x: 0, y: 0 };
  isLoading: boolean = false;
  error: string | null = null;
  
  // Trailer Configuration
  trailerDimensions: TrailerDimensions = { 
    length: 40, 
    width: 8.5, 
    maxWeight: 48000 
  };
  
  // Panel arrangement in trailer (local state for building)
  loadedPanels: LoadedPanel[] = [];
  
  // Search/filter state
  panelSearchQuery: string = "";
  panelFilter: {
    jobId?: string;
    minWeight?: number;
    maxWeight?: number;
  } = {};

  // Dialog form data
  dialogFormData: Record<string, any> = {};

  // Predefined trailer configurations (mock data until backend provides)
  predefinedTrailers: TrailerConfiguration[] = [
    {
      id: "40ft-flatbed",
      name: "40ft Flatbed",
      type: "flatbed",
      length: 40,
      width: 8.5,
      deckHeight: 60,
      maxCapacity: 48000,
      axleConfiguration: {
        front: { position: 5, maxLoad: 12000 },
        rear: { position: 35, maxLoad: 36000 }
      }
    },
    {
      id: "48ft-flatbed",
      name: "48ft Flatbed",
      type: "flatbed",
      length: 48,
      width: 8.5,
      deckHeight: 60,
      maxCapacity: 48000,
      axleConfiguration: {
        front: { position: 5, maxLoad: 12000 },
        rear: { position: 40, maxLoad: 36000 }
      }
    },
    {
      id: "53ft-flatbed",
      name: "53ft Flatbed",
      type: "flatbed",
      length: 53,
      width: 8.5,
      deckHeight: 60,
      maxCapacity: 48000,
      axleConfiguration: {
        front: { position: 5, maxLoad: 12000 },
        rear: { position: 45, maxLoad: 36000 }
      }
    },
    {
      id: "53ft-step-deck",
      name: "53ft Step Deck",
      type: "step-deck",
      length: 53,
      width: 8.5,
      deckHeight: 36,
      maxCapacity: 48000,
      axleConfiguration: {
        front: { position: 11, maxLoad: 12000 },
        rear: { position: 45, maxLoad: 36000 }
      }
    }
  ];

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
  }

  // Actions
  setActiveLoad = (id: string | null) => {
    this.activeLoadId = id;
    if (id) {
      // Load the saved configuration
      const load = this.activeLoad;
      if (load) {
        this.loadedPanels = load.panels || [];
        this.selectedTrailerConfigId = load.trailerConfigId;
        const config = this.predefinedTrailers.find(t => t.id === load.trailerConfigId);
        if (config) {
          this.trailerDimensions = {
            length: config.length,
            width: config.width,
            maxWeight: config.maxCapacity
          };
        }
      }
    }
  };

  setSelectedPanel = (id: string | null) => {
    this.selectedPanelId = id;
  };

  setTrailerConfig = (configId: string) => {
    const config = this.predefinedTrailers.find(t => t.id === configId);
    if (config) {
      this.trailerDimensions = {
        length: config.length,
        width: config.width,
        maxWeight: config.maxCapacity
      };
      this.selectedTrailerConfigId = configId;
    }
  };

  setCustomTrailerDimensions = (dimensions: Partial<TrailerDimensions>) => {
    this.trailerDimensions = { ...this.trailerDimensions, ...dimensions };
    this.selectedTrailerConfigId = null; // Clear predefined selection
  };

  setViewMode = (mode: 'top' | 'side' | '3d') => {
    this.viewMode = mode;
  };

  updateZoom = (level: number) => {
    this.zoomLevel = Math.max(0.5, Math.min(3, level));
  };

  updatePan = (x: number, y: number) => {
    this.panPosition = { x, y };
  };

  resetView = () => {
    this.zoomLevel = 1;
    this.panPosition = { x: 0, y: 0 };
  };

  setLoading = (loading: boolean) => {
    this.isLoading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setPanelSearchQuery = (query: string) => {
    this.panelSearchQuery = query;
  };

  setPanelFilter = (filter: typeof this.panelFilter) => {
    this.panelFilter = filter;
  };

  addPanelToLoad = (panelId: string) => {
    const panel = this.getPanelById(panelId);
    if (!panel) return;
    
    const position = this.findOptimalPosition(panelId);
    this.loadedPanels.push({
      panelId,
      position,
      rotation: 0,
      stackOrder: this.loadedPanels.length
    });
  };

  removePanelFromLoad = (panelId: string) => {
    this.loadedPanels = this.loadedPanels.filter(p => p.panelId !== panelId);
    this.reindexStackOrder();
    this.recalculatePositions();
  };

  movePanelInStack = (panelId: string, direction: 'up' | 'down') => {
    const index = this.loadedPanels.findIndex(p => p.panelId === panelId);
    if (index === -1) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= this.loadedPanels.length) return;
    
    [this.loadedPanels[index], this.loadedPanels[newIndex]] = 
    [this.loadedPanels[newIndex], this.loadedPanels[index]];
    
    this.reindexStackOrder();
    this.recalculatePositions();
  };

  rotatePanel = (panelId: string) => {
    const panel = this.loadedPanels.find(p => p.panelId === panelId);
    if (panel) {
      panel.rotation = ((panel.rotation + 90) % 360) as 0 | 90 | 180 | 270;
      this.recalculatePositions();
    }
  };

  clearLoad = () => {
    this.loadedPanels = [];
    this.selectedPanelId = null;
  };

  // Helper methods
  private reindexStackOrder = () => {
    this.loadedPanels.forEach((panel, index) => {
      panel.stackOrder = index;
    });
  };

  private findOptimalPosition = (panelId: string): { x: number; y: number } => {
    const panel = this.getPanelById(panelId);
    if (!panel) return { x: 0, y: 0 };
    
    const panelDimensions = this.getPanelDimensions(panel);
    
    // Start from front left of trailer
    let currentX = 0;
    let currentY = 0;
    
    // Find leftmost position that doesn't overlap
    for (const loadedPanel of this.loadedPanels) {
      const occupied = this.getOccupiedSpace(loadedPanel);
      
      if (this.wouldOverlap(currentX, currentY, panelDimensions, occupied)) {
        // Move to next available position
        currentX = occupied.x + occupied.width;
        
        // Wrap to next "row" if needed
        if (currentX + panelDimensions.width > this.trailerDimensions.width) {
          currentX = 0;
          currentY = Math.max(currentY, occupied.y + occupied.height);
        }
      }
    }
    
    return { x: currentX, y: currentY };
  };

  private recalculatePositions = () => {
    // Reflow all panels based on stack order and rotations
    const tempPanels = [...this.loadedPanels];
    this.loadedPanels = [];
    
    tempPanels.sort((a, b) => a.stackOrder - b.stackOrder);
    
    tempPanels.forEach(panel => {
      const position = this.findOptimalPosition(panel.panelId);
      this.loadedPanels.push({
        ...panel,
        position
      });
    });
  };

  private getOccupiedSpace = (panel: LoadedPanel): { x: number; y: number; width: number; height: number } => {
    const panelData = this.getPanelById(panel.panelId);
    if (!panelData) return { x: 0, y: 0, width: 0, height: 0 };
    
    const dimensions = this.getPanelDimensions(panelData);
    
    // Apply rotation
    const isRotated = panel.rotation === 90 || panel.rotation === 270;
    const width = isRotated ? dimensions.length : dimensions.width;
    const height = isRotated ? dimensions.width : dimensions.length;
    
    return {
      x: panel.position.x,
      y: panel.position.y,
      width,
      height
    };
  };

  private wouldOverlap = (
    x: number, 
    y: number, 
    dimensions: PanelDimensions, 
    occupied: { x: number; y: number; width: number; height: number }
  ): boolean => {
    return !(
      x + dimensions.width <= occupied.x ||
      x >= occupied.x + occupied.width ||
      y + dimensions.length <= occupied.y ||
      y >= occupied.y + occupied.height
    );
  };

  private getPanelDimensions = (panel: any): PanelDimensions => {
    // Mock dimensions until backend provides actual data
    // Using panelLength field as a hint, parse it if available
    let width = 8; // default width in feet
    let length = 20; // default length in feet
    
    if (panel.panelLength) {
      // Parse format like "20ft" or "20'"
      const lengthMatch = panel.panelLength.match(/(\d+)/);
      if (lengthMatch) {
        length = parseInt(lengthMatch[1]);
      }
    }
    
    // Estimate based on square footage if available
    if (panel.totalSqFt) {
      // Assuming panels are roughly 8ft wide
      length = panel.totalSqFt / width;
    }
    
    return {
      width,
      length,
      thickness: 6, // inches, default
      weight: panel.individualPanelWeight || 5000 // default 5000 lbs
    };
  };

  private getPanelById = (panelId: string): any => {
    // Access panels from service store
    const panelStore = this.rootStore.panelStore as any;
    const panels = panelStore?.entities || [];
    return panels.find((p: any) => 
      p.id === panelId || 
      p.panelId === panelId
    );
  };

  private calculateTotalPanelArea = (): number => {
    return this.loadedPanels.reduce((sum, panel) => {
      const panelData = this.getPanelById(panel.panelId);
      if (!panelData) return sum;
      
      const dimensions = this.getPanelDimensions(panelData);
      return sum + (dimensions.width * dimensions.length);
    }, 0);
  };

  private calculateAxleLoads = () => {
    const config = this.selectedTrailerConfig;
    if (!config) {
      // Default axle positions
      return {
        front: this.totalWeight * 0.25,
        rear: this.totalWeight * 0.75
      };
    }
    
    // Calculate based on center of gravity and axle positions
    const totalWeight = this.totalWeight;
    const centerOfGravity = this.calculateCenterOfGravity();
    
    // Simple moment calculation
    const frontAxlePos = config.axleConfiguration.front.position;
    const rearAxlePos = config.axleConfiguration.rear.position;
    const axleDistance = rearAxlePos - frontAxlePos;
    
    const rearLoad = (totalWeight * (centerOfGravity.x - frontAxlePos)) / axleDistance;
    const frontLoad = totalWeight - rearLoad;
    
    return {
      front: Math.max(0, frontLoad),
      rear: Math.max(0, rearLoad)
    };
  };

  private calculateCenterOfGravity = (): { x: number; y: number } => {
    if (this.loadedPanels.length === 0) {
      return { x: this.trailerDimensions.length / 2, y: this.trailerDimensions.width / 2 };
    }
    
    let totalWeight = 0;
    let weightedX = 0;
    let weightedY = 0;
    
    this.loadedPanels.forEach(panel => {
      const panelData = this.getPanelById(panel.panelId);
      if (!panelData) return;
      
      const dimensions = this.getPanelDimensions(panelData);
      const weight = dimensions.weight;
      
      // Calculate center position of panel
      const centerX = panel.position.x + dimensions.width / 2;
      const centerY = panel.position.y + dimensions.length / 2;
      
      totalWeight += weight;
      weightedX += centerX * weight;
      weightedY += centerY * weight;
    });
    
    if (totalWeight === 0) {
      return { x: this.trailerDimensions.length / 2, y: this.trailerDimensions.width / 2 };
    }
    
    return {
      x: weightedX / totalWeight,
      y: weightedY / totalWeight
    };
  };

  // Async actions
  saveLoadConfiguration = async (name: string, notes?: string) => {
    try {
      this.setLoading(true);
      
      const loadData = {
        name,
        accountId: this.rootStore.currentAccountId,
        jobId: this.rootStore.currentJobId,
        trailerConfigId: this.selectedTrailerConfigId,
        trailerDimensions: this.trailerDimensions,
        panels: this.loadedPanels,
        totalWeight: this.totalWeight,
        weightDistribution: this.weightDistribution,
        centerOfGravity: this.calculateCenterOfGravity(),
        status: 'draft',
        notes,
        createdBy: this.rootStore.currentUserId,
        createdAt: new Date()
      };
      
      // This will use the trailer-loads service when available
      // For now, store in local storage
      const savedLoads = JSON.parse(localStorage.getItem('trailerLoads') || '[]');
      const newLoad = {
        ...loadData,
        id: `load-${Date.now()}`
      };
      savedLoads.push(newLoad);
      localStorage.setItem('trailerLoads', JSON.stringify(savedLoads));
      
      this.activeLoadId = newLoad.id;
      
      return newLoad;
    } catch (error) {
      console.error('Error saving load configuration:', error);
      this.setError('Failed to save load configuration');
      throw error;
    } finally {
      this.setLoading(false);
    }
  };

  loadSavedConfiguration = async (loadId: string) => {
    try {
      this.setLoading(true);
      
      // This will use the trailer-loads service when available
      // For now, load from local storage
      const savedLoads = JSON.parse(localStorage.getItem('trailerLoads') || '[]');
      const load = savedLoads.find((l: any) => l.id === loadId);
      
      if (load) {
        runInAction(() => {
          this.activeLoadId = load.id;
          this.selectedTrailerConfigId = load.trailerConfigId;
          this.trailerDimensions = load.trailerDimensions;
          this.loadedPanels = load.panels;
        });
      }
      
      return load;
    } catch (error) {
      console.error('Error loading configuration:', error);
      this.setError('Failed to load configuration');
      throw error;
    } finally {
      this.setLoading(false);
    }
  };

  // Computed properties - Reference ServiceStores
  get activeLoad() {
    // When service is available:
    // return this.rootStore.trailerLoadsStore?.entities.find(
    //   load => load.id === this.activeLoadId
    // );
    
    // For now, use local storage
    const savedLoads = JSON.parse(localStorage.getItem('trailerLoads') || '[]');
    return savedLoads.find((l: any) => l.id === this.activeLoadId);
  }

  get availablePanels() {
    // Access panels from panel service store
    const panelStore = this.rootStore.panelStore as any;
    const panels = panelStore?.entities || [];
    
    // Filter based on search and filter criteria
    return panels.filter((panel: any) => {
      // Check if panel is available (not already loaded)
      if (panel.loadId || panel.loadNumber) return false;
      
      // Apply search query
      if (this.panelSearchQuery) {
        const query = this.panelSearchQuery.toLowerCase();
        const searchableFields = [
          panel.panelId,
          panel.markNumber,
          panel.controlNumber,
          panel.productType
        ].filter(Boolean).join(' ').toLowerCase();
        
        if (!searchableFields.includes(query)) return false;
      }
      
      // Apply filters
      if (this.panelFilter.jobId && panel.jobId !== this.panelFilter.jobId) {
        return false;
      }
      
      const weight = panel.individualPanelWeight || 0;
      if (this.panelFilter.minWeight && weight < this.panelFilter.minWeight) {
        return false;
      }
      if (this.panelFilter.maxWeight && weight > this.panelFilter.maxWeight) {
        return false;
      }
      
      return true;
    });
  }

  get selectedTrailerConfig() {
    return this.predefinedTrailers.find(t => t.id === this.selectedTrailerConfigId);
  }

  get totalWeight() {
    return this.loadedPanels.reduce((sum, item) => {
      const panel = this.getPanelById(item.panelId);
      if (!panel) return sum;
      const dimensions = this.getPanelDimensions(panel);
      return sum + dimensions.weight;
    }, 0);
  }

  get spaceUtilization() {
    const trailerArea = this.trailerDimensions.length * this.trailerDimensions.width;
    const panelArea = this.calculateTotalPanelArea();
    return trailerArea > 0 ? (panelArea / trailerArea) * 100 : 0;
  }

  get weightDistribution() {
    return this.calculateAxleLoads();
  }

  get isOverweight() {
    return this.totalWeight > this.trailerDimensions.maxWeight;
  }

  get frontAxleOverweight() {
    const config = this.selectedTrailerConfig;
    if (!config) return false;
    return this.weightDistribution.front > config.axleConfiguration.front.maxLoad;
  }

  get rearAxleOverweight() {
    const config = this.selectedTrailerConfig;
    if (!config) return false;
    return this.weightDistribution.rear > config.axleConfiguration.rear.maxLoad;
  }

  get savedLoads() {
    // This will use the trailer-loads service when available
    // For now, use local storage
    return JSON.parse(localStorage.getItem('trailerLoads') || '[]');
  }
}

export default TrailerLoadViewStore;